'use client';

import Link from 'next/link';
import { usePathname, useRouter } from 'next/navigation';
import { useState } from 'react';
import { useLibraryStore } from '@/stores/library-store';
import { useUiStore } from '@/stores/ui-store';
import { useRecentBooksStore } from '@/stores/recent-books-store';
import { useReadingProgressStore } from '@/stores/reading-progress-store';
import { useReaderStore } from '@/stores/reader-store';
import { downloadBook } from '@/lib/utils/fetch';
import { formatOpdsError } from '@/lib/utils/opds-errors';

function relativeTime(ts: number): string {
  const diff = Date.now() - ts;
  const mins = Math.floor(diff / 60_000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 30) return `${days}d ago`;
  return `${Math.floor(days / 30)}mo ago`;
}

export function Sidebar() {
  const libraries = useLibraryStore((s) => s.libraries);
  const getLibrary = useLibraryStore((s) => s.getLibrary);
  const { sidebarOpen, setSidebarOpen } = useUiStore();
  const pathname = usePathname();
  const router = useRouter();

  const recentBooks = useRecentBooksStore((s) => s.recentBooks);
  const removeRecentBook = useRecentBooksStore((s) => s.removeRecentBook);
  const progress = useReadingProgressStore((s) => s.progress);
  const openBook = useReaderStore((s) => s.openBook);

  const [loadingBook, setLoadingBook] = useState<string | null>(null);

  // Filter: only show books that have been read past page 0
  const openBooks = recentBooks.filter((book) => {
    const p = progress[book.bookKey];
    return p && p.page > 0;
  });

  const handleOpenRecent = async (book: typeof recentBooks[0]) => {
    setLoadingBook(book.bookKey);
    try {
      const lib = getLibrary(book.libraryId);
      const blob = await downloadBook(book.downloadUrl, lib);
      openBook(blob, book.fileName, book.title, book.authors, book.downloadUrl, book.libraryId);
      setSidebarOpen(false);
      router.push('/reader');
    } catch (e) {
      alert(formatOpdsError(e));
    } finally {
      setLoadingBook(null);
    }
  };

  return (
    <>
      {/* Mobile overlay */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 z-30 bg-black/40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      <aside
        className={`fixed top-0 left-0 z-40 flex h-full w-64 flex-col border-r border-neutral-200 bg-white transition-transform lg:translate-x-0 ${
          sidebarOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
      >
        <div className="flex h-14 items-center gap-2 border-b border-neutral-100 px-4">
          <svg className="h-6 w-6 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
          </svg>
          <Link href="/" className="text-2xl font-bold text-neutral-900" style={{ fontFamily: 'var(--font-caveat)' }} onClick={() => setSidebarOpen(false)}>
            SBFLibrary
          </Link>
        </div>

        <nav className="flex-1 overflow-y-auto p-3">
          <div className="mb-2 px-2 text-xs font-semibold uppercase tracking-wider text-neutral-400">
            Libraries
          </div>

          {libraries.length === 0 ? (
            <p className="px-2 text-sm text-neutral-400">No libraries added yet</p>
          ) : (
            <ul className="space-y-0.5">
              {libraries.map((lib) => {
                const isActive = pathname.startsWith(`/library/${lib.id}`);
                return (
                  <li key={lib.id}>
                    <Link
                      href={`/library/${lib.id}`}
                      onClick={() => setSidebarOpen(false)}
                      className={`flex items-center gap-2 rounded-lg px-2 py-2 text-sm transition-colors ${
                        isActive
                          ? 'bg-blue-50 text-blue-700 font-medium'
                          : 'text-neutral-700 hover:bg-neutral-50'
                      }`}
                    >
                      <svg className="h-4 w-4 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M8 14v3m4-3v3m4-3v3M3 21h18M3 10h18M3 7l9-4 9 4M4 10h16v11H4V10z" />
                      </svg>
                      <span className="truncate">{lib.name}</span>
                    </Link>
                  </li>
                );
              })}
            </ul>
          )}

          {/* Open Books section */}
          {openBooks.length > 0 && (
            <>
              <div className="mb-2 mt-6 px-2 text-xs font-semibold uppercase tracking-wider text-neutral-400">
                Open Books
              </div>
              <ul className="space-y-0.5">
                {openBooks.map((book) => {
                  const isLoading = loadingBook === book.bookKey;

                  return (
                    <li key={book.bookKey} className="group relative">
                      <button
                        onClick={() => handleOpenRecent(book)}
                        disabled={isLoading}
                        className="flex w-full items-start gap-2 rounded-lg px-2 py-2 pr-7 text-left text-sm text-neutral-700 transition-colors hover:bg-neutral-50 disabled:opacity-50"
                      >
                        {/* Book icon */}
                        <svg className="mt-0.5 h-4 w-4 shrink-0 text-neutral-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                          <path strokeLinecap="round" strokeLinejoin="round" d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                        </svg>
                        <div className="min-w-0 flex-1">
                          <div className="truncate text-sm font-medium">
                            {isLoading ? 'Loading…' : book.title}
                          </div>
                          {book.authors.length > 0 && (
                            <div className="truncate text-xs text-neutral-400">
                              {book.authors.join(', ')}
                            </div>
                          )}
                          <div className="text-xs text-neutral-400">
                            {relativeTime(book.lastOpened)}
                          </div>
                        </div>
                      </button>
                      {/* Remove button */}
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          removeRecentBook(book.bookKey);
                        }}
                        className="absolute right-1 top-1/2 -translate-y-1/2 rounded p-1 text-neutral-400 hover:text-neutral-600 lg:opacity-0 lg:transition-opacity lg:group-hover:opacity-100"
                        title="Remove from list"
                      >
                        <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                          <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </button>
                    </li>
                  );
                })}
              </ul>
            </>
          )}
        </nav>

        <div className="border-t border-neutral-100 p-3">
          <Link
            href="/"
            onClick={() => setSidebarOpen(false)}
            className="flex items-center gap-2 rounded-lg px-2 py-2 text-sm text-neutral-600 hover:bg-neutral-50"
          >
            <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.066 2.573c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.573 1.066c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.066-2.573c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
              <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
            </svg>
            Manage Libraries
          </Link>
        </div>
      </aside>
    </>
  );
}
